import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, PenTool } from "lucide-react";
import { FIRMA, applyCompany } from "@/lib/branding";
import { computeAmounts, fmtCurrency, fmtDateTime, quoteNet } from "@/lib/quote-helpers";
import { previewNodeToPdfBase64 } from "@/lib/pdf";
import { API } from "@/lib/api";
import { LegalContent } from "@/components/LegalContent";
import { SignaturePad } from "@/components/SignaturePad";
import { docLabel } from "@/lib/constants";
import { QuotePreview } from "@/components/QuotePreview";
import { MobileQuoteView } from "@/components/MobileQuoteView";

const PublicQuote = () => {
  const { nr, token } = useParams();
  const apiPath = token
    ? `quote-by-token/${encodeURIComponent(token)}`
    : `quote/${encodeURIComponent(nr)}`;
  const [quote, setQuote] = useState(null);
  const [steuer, setSteuer] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [signature, setSignature] = useState({});
  const [payCfg, setPayCfg] = useState({});
  const [paying, setPaying] = useState(false);
  const [payMsg, setPayMsg] = useState("");
  const [signOpen, setSignOpen] = useState(false);
  const [sigDraft, setSigDraft] = useState("");
  const [sigName, setSigName] = useState("");
  const [error, setError] = useState(null);
  const [company, setCompany] = useState({});
  const [legalView, setLegalView] = useState(null);
  const previewRef = useRef(null);
  const docScaleRef = useRef(null);
  const [docZoom, setDocZoom] = useState(1);
  const [docH, setDocH] = useState(0);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  const [docView, setDocView] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  useEffect(() => {
    const el = docScaleRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const update = () => {
      setDocZoom(Math.min(1, (el.clientWidth || 794) / 794));
      if (previewRef.current) setDocH(previewRef.current.offsetHeight || 0);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (previewRef.current) ro.observe(previewRef.current);
    return () => ro.disconnect();
  }, [quote, isMobile, docView]);
  const showDocument = !isMobile || docView;
  const canPayBar = (q) =>
    q && q.status !== "paid" && (payCfg.stripe_enabled || payCfg.paypal_enabled) && total > 0;
  const canSignBar = (q) => q && !q.customer_signed_at && q.dokument_typ !== "rechnung";
  const total = useMemo(() => quote ? quoteNet(quote) : 0, [quote]);

  const reloadQuote = async () => {
    const q = await axios.get(`${API}/public/${apiPath}`);
    setQuote(q.data);
    return q.data;
  };

  useEffect(() => {
    (async () => {
      try {
        const [q, s, a, sig, comp, pay] = await Promise.all([
          axios.get(`${API}/public/${apiPath}`),
          axios.get(`${API}/settings/steuerdaten`).catch(() => ({ data: {} })),
          axios.get(`${API}/settings/attachments`).catch(() => ({ data: [] })),
          axios.get(`${API}/settings/signature`).catch(() => ({ data: {} })),
          axios.get(`${API}/settings/company`).catch(() => ({ data: null })),
          axios.get(`${API}/settings/payment`).catch(() => ({ data: {} })),
        ]);
        if (comp.data) applyCompany(comp.data);
        setCompany(comp.data || {});
        setQuote(q.data);
        setSteuer(s.data || {});
        setAttachments(a.data || []);
        setSignature(sig.data || {});
        setPayCfg(pay.data || {});
        // payment return polling
        const sid = new URLSearchParams(window.location.search).get("session_id");
        if (sid) {
          setPayMsg("Zahlung wird geprüft …");
          let attempts = 0;
          const poll = async () => {
            attempts += 1;
            try {
              const st = await axios.get(`${API}/public/payment-status/${sid}`);
              if (st.data.payment_status === "paid") {
                setPayMsg("Zahlung erfolgreich – vielen Dank!");
                await reloadQuote();
                return;
              }
            } catch {}
            if (attempts < 6) setTimeout(poll, 2000);
            else setPayMsg("Zahlungsstatus konnte nicht bestätigt werden.");
          };
          poll();
        }
        // PayPal return: capture the approved order
        const ppParams = new URLSearchParams(window.location.search);
        if (ppParams.get("paypal") === "1" && ppParams.get("token")) {
          const orderId = ppParams.get("token");
          setPayMsg("PayPal-Zahlung wird abgeschlossen …");
          try {
            const cap = await axios.post(`${API}/public/paypal/capture/${orderId}`);
            if (cap.data.payment_status === "paid") {
              setPayMsg("Zahlung erfolgreich – vielen Dank!");
              await reloadQuote();
            } else {
              setPayMsg("PayPal-Zahlung wurde nicht abgeschlossen.");
            }
          } catch {
            setPayMsg("PayPal-Zahlung konnte nicht bestätigt werden.");
          }
        }
      } catch (e) {
        setError("Angebot nicht gefunden");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiPath]);

  const payNow = async (payKind = "full") => {
    setPaying(true);
    try {
      const res = await axios.post(
        `${API}/public/quote-by-token/${quote.public_token}/checkout`,
        { origin_url: window.location.origin, pay_kind: payKind }
      );
      window.location.href = res.data.url;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Zahlung konnte nicht gestartet werden");
      setPaying(false);
    }
  };

  const payNowPaypal = async (payKind = "full") => {
    setPaying(true);
    try {
      const res = await axios.post(
        `${API}/public/quote-by-token/${quote.public_token}/paypal/create`,
        { origin_url: window.location.origin, pay_kind: payKind }
      );
      window.location.href = res.data.url;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "PayPal-Zahlung konnte nicht gestartet werden");
      setPaying(false);
    }
  };

  const submitSignature = async () => {
    if (!sigDraft) {
      toast.error("Bitte zuerst unterschreiben");
      return;
    }
    let pdf_base64 = "";
    try {
      if (previewRef.current) pdf_base64 = await previewNodeToPdfBase64(previewRef.current);
    } catch (e) {
      console.error("PDF-Erstellung übersprungen", e);
    }
    try {
      const res = await axios.post(
        `${API}/public/${apiPath}/sign`,
        { data_url: sigDraft, signed_name: sigName, pdf_base64, origin_url: window.location.origin }
      );
      setQuote(res.data);
      setSignOpen(false);
      toast.success(`${docLabel(quote.dokument_typ, company)} angenommen – vielen Dank!`);
    } catch {
      toast.error("Konnte Unterschrift nicht speichern");
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--brand-bg)] font-[Inter]">
        <div className="text-center">
          <h1 className="font-[Playfair_Display] text-3xl mb-2">
            Angebot nicht gefunden
          </h1>
          <p className="text-[var(--brand-muted)]">
            Bitte prüfe den Link oder kontaktiere den Absender.
          </p>
        </div>
      </div>
    );
  }
  if (!quote) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--brand-bg)]">
        <span className="text-[var(--brand-muted)]">Wird geladen …</span>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[var(--brand-border)] py-8 font-[Inter]"
      style={{ paddingBottom: isMobile && (canPayBar(quote) || canSignBar(quote)) ? 100 : undefined }}
    >
      <div className="max-w-[820px] mx-auto px-4">
        <div className="mb-4 text-center text-xs text-[var(--brand-muted)] uppercase tracking-wider">
          Persönliche {docLabel(quote.dokument_typ, company)} für{" "}
          {quote.kunde_firma || quote.kunde_name || "Sie"}
        </div>
        {isMobile && (
          <div className="text-center mb-3">
            <button
              onClick={() => setDocView((v) => !v)}
              className="text-xs underline text-[var(--brand-muted)]"
              data-testid="toggle-doc-view"
            >
              {docView ? "Zur kompakten Ansicht" : "Original-Dokument ansehen"}
            </button>
          </div>
        )}
        {!showDocument && (
          <MobileQuoteView quote={quote} company={company} steuer={steuer} total={total} signature={signature} />
        )}
        <div
          ref={docScaleRef}
          aria-hidden={!showDocument}
          style={
            showDocument
              ? { width: "100%" }
              : { position: "absolute", left: "-20000px", top: 0, width: "794px" }
          }
        >
          <div
            style={{
              height: showDocument && docZoom < 1 && docH ? docH * docZoom : "auto",
              overflow: "hidden",
            }}
          >
            <div style={{ width: 794, transform: `scale(${docZoom})`, transformOrigin: "top left" }}>
              <QuotePreview ref={previewRef} quote={quote} total={total} company={company} steuer={steuer} attachments={attachments} signature={signature} />
            </div>
          </div>
        </div>
        {quote.status === "paid" ? (
          <div
            className="text-center mt-6 text-sm rounded p-3 font-semibold"
            style={{ background: "#ECFDF5", color: "#059669" }}
            data-testid="paid-banner"
          >
            <CheckCircle2 className="w-5 h-5 inline-block mr-1" />
            Diese Rechnung wurde bezahlt. Vielen Dank!
          </div>
        ) : (
          (payCfg.stripe_enabled || payCfg.paypal_enabled) && total > 0 && (() => {
            const a = computeAmounts(quote);
            return (
            <div className="text-center mt-6 space-y-3">
              {payMsg && (
                <div className="mb-2 text-sm text-[var(--brand-muted)]" data-testid="pay-msg">{payMsg}</div>
              )}
              <div className="flex flex-wrap gap-3 justify-center">
                {a.deposit > 0 && quote.deposit_paid ? (
                  <>
                    {payCfg.stripe_enabled && (
                      <Button
                        onClick={() => payNow("rest")}
                        disabled={paying}
                        className="bg-[var(--brand-ink)] hover:bg-black text-white px-5 sm:px-8 py-3 rounded-full font-semibold tracking-wide whitespace-normal h-auto max-w-full text-center"
                        data-testid="btn-pay-rest"
                      >
                        {paying ? "Weiterleitung …" : `Restbetrag jetzt zahlen · ${fmtCurrency(a.rest, a.currency)}`}
                      </Button>
                    )}
                    {payCfg.paypal_enabled && (
                      <Button
                        onClick={() => payNowPaypal("rest")}
                        disabled={paying}
                        className="bg-[#0070BA] hover:bg-[#005EA6] text-white px-5 sm:px-8 py-3 rounded-full font-semibold tracking-wide whitespace-normal h-auto max-w-full text-center"
                        data-testid="btn-paypal-rest"
                      >
                        {paying ? "Weiterleitung …" : `Restbetrag mit PayPal · ${fmtCurrency(a.rest, a.currency)}`}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {a.deposit > 0 && payCfg.stripe_enabled && (
                      <Button
                        onClick={() => payNow("deposit")}
                        disabled={paying}
                        className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white px-7 py-3 rounded-full font-semibold tracking-wide"
                        data-testid="btn-pay-deposit"
                      >
                        {paying ? "Weiterleitung …" : `Anzahlung zahlen · ${fmtCurrency(a.deposit, a.currency)}`}
                      </Button>
                    )}
                    {a.deposit > 0 && payCfg.paypal_enabled && (
                      <Button
                        onClick={() => payNowPaypal("deposit")}
                        disabled={paying}
                        className="bg-[#0070BA] hover:bg-[#005EA6] text-white px-7 py-3 rounded-full font-semibold tracking-wide"
                        data-testid="btn-paypal-deposit"
                      >
                        {paying ? "Weiterleitung …" : `Anzahlung mit PayPal · ${fmtCurrency(a.deposit, a.currency)}`}
                      </Button>
                    )}
                    {payCfg.stripe_enabled && (
                      <Button
                        onClick={() => payNow("full")}
                        disabled={paying}
                        className="bg-[var(--brand-ink)] hover:bg-black text-white px-5 sm:px-8 py-3 rounded-full font-semibold tracking-wide whitespace-normal h-auto max-w-full text-center"
                        data-testid="btn-pay-now"
                      >
                        {paying ? "Weiterleitung …" : `${a.deposit > 0 ? "Gesamt bezahlen" : "Jetzt bezahlen"} · ${fmtCurrency(a.gross, a.currency)}`}
                      </Button>
                    )}
                    {payCfg.paypal_enabled && (
                      <Button
                        onClick={() => payNowPaypal("full")}
                        disabled={paying}
                        className="bg-[#0070BA] hover:bg-[#005EA6] text-white px-5 sm:px-8 py-3 rounded-full font-semibold tracking-wide whitespace-normal h-auto max-w-full text-center"
                        data-testid="btn-paypal-now"
                      >
                        {paying ? "Weiterleitung …" : `${a.deposit > 0 ? "Gesamt mit PayPal" : "Mit PayPal zahlen"} · ${fmtCurrency(a.gross, a.currency)}`}
                      </Button>
                    )}
                  </>
                )}
              </div>
              {quote.deposit_paid && !quote.rest_paid && (
                <div className="text-sm text-green-700" data-testid="deposit-paid-note">
                  ✓ Anzahlung erhalten – offener Restbetrag: {fmtCurrency(a.rest, a.currency)}
                </div>
              )}
            </div>
            );
          })()
        )}
        {!quote.customer_signed_at && quote.dokument_typ !== "rechnung" && (
          <div className="text-center mt-6 px-2">
            <Button
              onClick={() => setSignOpen(true)}
              className="w-full sm:w-auto max-w-full whitespace-normal h-auto bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white px-6 sm:px-5 sm:px-8 py-3 rounded-full font-semibold tracking-wide whitespace-normal h-auto max-w-full text-center"
              data-testid="btn-open-sign"
            >
              <PenTool className="w-4 h-4 mr-2 shrink-0" /> {docLabel(quote.dokument_typ, company)} annehmen &amp; unterschreiben
            </Button>
          </div>
        )}
        {quote.customer_signed_at && (
          <div
            className="text-center mt-6 text-sm rounded p-3"
            style={{ background: "var(--brand-bg)", color: "var(--brand-ink)" }}
          >
            <CheckCircle2 className="w-5 h-5 inline-block mr-1 text-[var(--brand-primary)]" />
            Sie haben das Dokument {docLabel(quote.dokument_typ, company)} am {fmtDateTime(quote.customer_signed_at)} angenommen.
          </div>
        )}
        {quote.reseller_link && (
          <div className="text-center mt-6">
            <a
              href={quote.reseller_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white px-5 sm:px-8 py-3 rounded-full font-semibold tracking-wide whitespace-normal h-auto max-w-full text-center"
              data-testid="public-pay-btn"
            >
              Jetzt sicher zahlen
            </a>
          </div>
        )}
        <div className="text-center mt-4 text-xs text-[var(--brand-muted)]">
          {FIRMA.firma} · {FIRMA.email} · {FIRMA.telefon}
        </div>
        <div className="text-center mt-2 text-xs">
          <button
            type="button"
            onClick={() => setLegalView("impressum")}
            className="text-[var(--brand-primary)] hover:underline"
            data-testid="link-impressum"
          >
            Impressum
          </button>
          <span className="text-[var(--brand-muted)] mx-2">·</span>
          <button
            type="button"
            onClick={() => setLegalView("datenschutz")}
            className="text-[var(--brand-primary)] hover:underline"
            data-testid="link-datenschutz"
          >
            Datenschutz
          </button>
          <span className="text-[var(--brand-muted)] mx-2">·</span>
          <button
            type="button"
            onClick={() => window.openCookieSettings && window.openCookieSettings()}
            className="text-[var(--brand-primary)] hover:underline"
            data-testid="link-cookie-settings"
          >
            Cookie-Einstellungen
          </button>
        </div>
      </div>

      {isMobile && (canPayBar(quote) || canSignBar(quote)) && (
        <div
          className="fixed bottom-0 inset-x-0 z-40 border-t border-[var(--brand-border)] bg-white/95 backdrop-blur-md px-3 pt-2.5"
          style={{ paddingBottom: "calc(0.625rem + env(safe-area-inset-bottom))" }}
          data-testid="mobile-action-bar"
        >
          <div className="flex gap-2 max-w-[820px] mx-auto">
            {canPayBar(quote) &&
              (() => {
                const a = computeAmounts(quote);
                const kind = a.deposit > 0 && quote.deposit_paid ? "rest" : a.deposit > 0 ? "deposit" : "full";
                const amount = kind === "rest" ? a.rest : kind === "deposit" ? a.deposit : a.gross;
                const label = kind === "rest" ? "Restbetrag zahlen" : kind === "deposit" ? "Anzahlung zahlen" : "Jetzt bezahlen";
                const handler = payCfg.stripe_enabled ? payNow : payNowPaypal;
                return (
                  <Button
                    onClick={() => handler(kind)}
                    disabled={paying}
                    className="flex-1 bg-[var(--brand-ink)] hover:bg-black text-white rounded-full font-semibold py-3 h-auto whitespace-normal text-sm"
                    data-testid="mobile-action-pay"
                  >
                    {paying ? "Weiterleitung …" : `${label} · ${fmtCurrency(amount, a.currency)}`}
                  </Button>
                );
              })()}
            {canSignBar(quote) && (
              <Button
                onClick={() => setSignOpen(true)}
                className="flex-1 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white rounded-full font-semibold py-3 h-auto whitespace-normal text-sm"
                data-testid="mobile-action-sign"
              >
                <PenTool className="w-4 h-4 mr-1.5 shrink-0" /> Annehmen
              </Button>
            )}
          </div>
        </div>
      )}

      {legalView && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
          onClick={() => setLegalView(null)}
          data-testid="legal-modal"
        >
          <Card
            className="p-6 bg-white border-[var(--brand-border)] w-full max-w-2xl max-h-[85vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <LegalContent view={legalView} company={company} steuer={steuer} />
            <div className="flex justify-end pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLegalView(null)}
                data-testid="legal-close"
              >
                Schließen
              </Button>
            </div>
          </Card>
        </div>
      )}

      {signOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
          onClick={() => setSignOpen(false)}
        >
          <Card
            className="p-6 bg-white border-[var(--brand-border)] w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-[Playfair_Display] text-xl font-semibold mb-2">
              {docLabel(quote.dokument_typ, company)} annehmen
            </h3>
            <p className="text-xs text-[var(--brand-muted)] mb-3">
              Mit Ihrer Unterschrift bestätigen Sie verbindlich die Annahme
              des Dokuments {docLabel(quote.dokument_typ, company)}.
            </p>
            <div className="mb-3">
              <Label>Ihr Name (optional)</Label>
              <Input
                value={sigName}
                onChange={(e) => setSigName(e.target.value)}
                placeholder="Max Mustermann"
                data-testid="customer-sign-name"
              />
            </div>
            <SignaturePad onChange={setSigDraft} />
            <div className="flex gap-2 justify-end mt-4">
              <Button variant="outline" size="sm" onClick={() => setSignOpen(false)}>
                Abbrechen
              </Button>
              <Button
                size="sm"
                onClick={submitSignature}
                className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
                data-testid="customer-sign-submit"
              >
                Verbindlich annehmen
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export { PublicQuote };
